const weaponImageContext = require.context("../assets/items/weapon", false, /\.png$/);
const itemCatalog = require("./itemCatalog.json");
const verifiedItemMap = require("./verifiedItemMap.json");
const { findItemStatsDraft } = require("../utils/itemStatsDraftMerge");
const rawWeaponFiles = weaponImageContext.keys().map((path) => {
  const imageModule = weaponImageContext(path);

  return {
    fileName: path.replace("./", ""),
    image: imageModule.default || imageModule,
  };
});

const WEAPON_NAME_TO_ITEM_CODE = {
  deathadder: "131301",
  deathadderqueen: "131401",
  blackmambaking: "131402",
  alphasiderwinder: "131403",
  alphasidewinder: "131403",
  deathadderqueenmt: "131501",
  deathadderqueenfc: "131502",
  deathadderqueenvbs: "131503",
  blackmambakingtl: "131504",
  blackmambakingfc: "131505",
  blackmambakingvbs: "131506",
  alphasiderwinderml: "131507",
  alphasidewinderml: "131507",
  alphasidewinderfc: "131508",
  alphasidewindervbs: "131509",
};

const ALLOWED_SELECT_GRADES = new Set(["epic", "legend", "mythic"]);

const GRADE_LABELS = {
  common: "\uc77c\ubc18",
  uncommon: "\uace0\uae09",
  rare: "\ud76c\uadc0",
  epic: "\uc601\uc6c5",
  legend: "\uc804\uc124",
  mythic: "\ucd08\uc6d4",
  unknown: "\ub4f1\uae09 \ubbf8\ud655\uc778",
};

const catalogByCode = itemCatalog.reduce((catalog, item) => {
  const code = String(item.item_code || item.code || "").trim();

  if (!code) {
    return catalog;
  }

  catalog[code] = item;
  return catalog;
}, {});

export function getWeaponKeyFromFileName(fileName) {
  return String(fileName || "")
    .replace(/\.[^.]+$/, "")
    .replace(/^\d+\s*\./, "")
    .trim();
}

export function normalizeWeaponName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[\s_.-]/g, "");
}

function normalizeItemGrade(grade) {
  const normalized = String(grade || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

  const aliases = {
    common: "common",
    normal: "common",
    basic: "common",
    uncommon: "uncommon",
    advanced: "uncommon",
    rare: "rare",
    epic: "epic",
    heroic: "epic",
    legend: "legend",
    legendary: "legend",
    mythic: "mythic",
    "\uc77c\ubc18": "common",
    "\uace0\uae09": "uncommon",
    "\ud76c\uadc0": "rare",
    "\uc601\uc6c5": "epic",
    "\uc804\uc124": "legend",
    "\ucd08\uc6d4": "mythic",
  };

  return aliases[normalized] || "unknown";
}

function getGradeLabel(grade) {
  return GRADE_LABELS[normalizeItemGrade(grade)] || GRADE_LABELS.unknown;
}

function normalizeWeaponKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/siderwinder/g, "sidewinder")
    .replace(/black\s*mamba/g, "blackmamba")
    .replace(/death\s*adder/g, "deathadder")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getBaseWeaponType(weaponKey) {
  const normalizedName = normalizeWeaponName(weaponKey);

  if (normalizedName.includes("deathadder")) {
    return "deathadder";
  }

  if (normalizedName.includes("sidewinder") || normalizedName.includes("siderwinder")) {
    return "sidewinder";
  }

  if (normalizedName.includes("blackmamba")) {
    return "blackmamba";
  }

  return "";
}

function formatWeaponName(weaponKey) {
  return String(weaponKey || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      const lowerPart = part.toLowerCase();

      if (lowerPart === "deathadder") return "DeathAdder";
      if (lowerPart === "sidewinder") return "Sidewinder";
      if (lowerPart === "blackmamba") return "Black Mamba";
      if (["mt", "fc", "tl", "vbs", "ml"].includes(lowerPart)) return lowerPart.toUpperCase();

      return lowerPart.charAt(0).toUpperCase() + lowerPart.slice(1);
    })
    .join(" ");
}

function formatWeaponDisplayName(name) {
  return String(name || "").replace(/Alpha Sidewinder/gi, "Super Sidewinder");
}

function createWeaponAssets() {
  return rawWeaponFiles.reduce((assets, file) => {
    const rawWeaponName = getWeaponKeyFromFileName(file.fileName);
    const weaponKey = normalizeWeaponKey(rawWeaponName);
    const baseWeaponType = getBaseWeaponType(weaponKey);
    const itemCode = WEAPON_NAME_TO_ITEM_CODE[normalizeWeaponName(weaponKey)] || "";
    const catalog = itemCode ? catalogByCode[itemCode] : null;
    const verified = verifiedItemMap[`weapon/${file.fileName}`] || null;

    if (!baseWeaponType) {
      console.warn(`[weaponAssets] unmatched weapon image: ${file.fileName}`);
      return assets;
    }

    const name = formatWeaponName(weaponKey);
    const verifiedGrade = normalizeItemGrade(verified?.grade);
    const catalogGrade = normalizeItemGrade(catalog?.item_grade || catalog?.grade);
    const grade = verifiedGrade !== "unknown" ? verifiedGrade : catalogGrade;
    const displayName = verified?.displayName || formatWeaponDisplayName(name);

    const weaponAsset = {
      weaponKey,
      weaponType: baseWeaponType,
      baseWeaponType,
      code: itemCode || weaponKey,
      itemCode,
      name,
      displayName,
      fileName: file.fileName,
      grade,
      gradeLabel: getGradeLabel(grade),
      slotType: "weapon",
      image: file.image,
    };
    const draft = findItemStatsDraft(weaponAsset);

    assets[weaponKey] = {
      ...weaponAsset,
      itemSkill: draft?.itemSkill || null,
      itemSkills: draft?.itemSkills || [],
      rawItemSkill: draft?.rawItemSkill || [],
      rawStats: draft?.rawStats || [],
      stats: draft?.stats || {},
      statsDraftCheck: draft?.check ?? null,
    };

    return assets;
  }, {});
}

export const weaponAssets = createWeaponAssets();
export const weaponAssetList = Object.values(weaponAssets);
export const weaponTypeOptions = [
  { weaponType: "deathadder", name: "DeathAdder" },
  { weaponType: "sidewinder", name: "Sidewinder" },
  { weaponType: "blackmamba", name: "BlackMamba" },
];

export function getWeaponAssetsByType(weaponType) {
  return weaponAssetList.filter((weapon) => (
    weapon.baseWeaponType === weaponType
    && ALLOWED_SELECT_GRADES.has(normalizeItemGrade(weapon.grade))
  ));
}

export function getDefaultWeaponKeyForType(weaponType) {
  return getWeaponAssetsByType(weaponType)[0]?.weaponKey || "";
}
